// What a lineup says about its locks (B-221, LINEUP.md). One place for the
// words, so the list, the table and the health bar never disagree.
//
// Every time here is the reader's and named (B-181).

import { formatInZone, whenInZone, type LineupRowView } from '@fsp/domain';

/** "Sun 11:00 AM MDT": when this row locked, or null while it has no lock or no zone. */
export function lockTime(row: LineupRowView, zone: string | null): string | null {
  if (row.lockAt === null || zone === null) return null;
  return formatInZone(row.lockAt, zone, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

/**
 * What a locked row says when it is tapped, and to a screen reader: "Locked at
 * Sun 11:00 AM MDT kickoff." (LINEUP.md) — "kickoff" only when the lock is his
 * own game's start. A league that locks at a fixed time, or at the week's
 * first game, and a pro bye, which locks with the week, say the time alone
 * (the review of 2026-09-27).
 */
export function lockWords(row: LineupRowView, zone: string | null): string {
  const at = lockTime(row, zone);
  if (at === null) return 'Locked.';
  const hisKickoff =
    row.game !== null &&
    row.game.startAt !== null &&
    row.lockAt !== null &&
    Date.parse(row.game.startAt) === Date.parse(row.lockAt);
  return hisKickoff ? `Locked at ${at} kickoff.` : `Locked at ${at}.`;
}

/**
 * The health bar's lock line: "3 starters locked · next lock Sun 2:25 PM MDT",
 * or "Next lock Thu 6:15 PM MDT" before any has. Nothing once the whole
 * lineup is locked (its headline says so), and no time while the zone is not
 * known, because a time in another zone is a wrong time.
 */
export function lockNote(
  locked: number | undefined,
  locksAt: string | null,
  zone: string | null,
  now: number,
): string | null {
  const next = locksAt !== null && zone !== null ? whenInZone(locksAt, zone, now) : null;
  if (locked !== undefined && locked > 0) {
    const count = `${locked} ${locked === 1 ? 'starter' : 'starters'} locked`;
    return next === null ? count : `${count} · next lock ${next}`;
  }
  return next === null ? null : `Next lock ${next}`;
}
